import React from "react";
import { Check } from "lucide-react";

type Props = {
  /** The A/B/C key Typeform shows beside every choice. */
  optionKey: string;
  label: React.ReactNode;
  isSelected: boolean;
  disabled?: boolean;
  /**
   * Whether picking this option replaces the client's answer or adds to it. It
   * only changes what assistive tech announces: a multi-select reads as a
   * checkbox, so the client hears that more than one choice is allowed, while an
   * exclusive choice reads as a toggle.
   */
  selectionMode: "single" | "multiple";
  onSelect: () => void;
};

/**
 * One choice on the client-facing intake, shared by every question that offers a
 * list to pick from -- yes/no, single-select, and multi-select.
 *
 * They render through one component rather than copies of the same class list
 * because a single-page intake stacks all three within a screen of each other,
 * where any drift between them reads as a rendering bug.
 */
export function TypeformOptionCard({
  optionKey,
  label,
  isSelected,
  disabled = false,
  selectionMode,
  onSelect
}: Props) {
  const isMultiple = selectionMode === "multiple";

  // A chosen option flips to a solid light fill with dark text, rather than a
  // slightly brighter translucent card. On a long list the earlier treatment --
  // a faint border and a dim check -- was almost invisible; the client couldn't
  // tell at a glance which criteria they'd picked. Inverting the whole card is
  // the one change that reads instantly against the navy background.
  const card = isSelected
    ? "border-white bg-white text-[#0f172a] shadow-[0_0_0_2px_#ffffff,0_10px_26px_rgba(0,0,0,0.28)]"
    : "border-white/10 bg-white/10 text-white backdrop-blur-xs hover:bg-white/16 active:bg-white/20";
  const badge = isSelected
    ? "border-[#0f172a]/20 bg-[#0f172a]/10 text-[#0f172a]"
    : "border-white/25 bg-white/5 text-white";
  const text = isSelected
    ? "text-[#0f172a] font-semibold"
    : "text-white font-body";

  return (
    <button
      type="button"
      disabled={disabled}
      role={isMultiple ? "checkbox" : undefined}
      aria-checked={isMultiple ? isSelected : undefined}
      aria-pressed={isMultiple ? undefined : isSelected}
      className={[
        "flex min-h-12 w-full items-center gap-3 rounded-lg border border-solid px-3 py-2.5 text-left transition",
        card,
        disabled ? "cursor-not-allowed opacity-60" : ""
      ].join(" ")}
      onClick={() => {
        if (disabled) return;
        onSelect();
      }}
    >
      <span
        className={`flex h-6 min-w-6 items-center justify-center rounded border border-solid px-1.5 text-xs font-semibold leading-none ${badge}`}
      >
        {optionKey}
      </span>
      <span className={`min-w-0 flex-1 text-body ${text}`}>{label}</span>
      {isSelected ? (
        <Check aria-hidden className="size-4 shrink-0 text-[#0f172a]" />
      ) : null}
    </button>
  );
}
